import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, EmptyState } from '@kainos/ui';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render errors so a single broken screen never blanks the whole app.
 * Errors are logged for the browser console; CloudWatch RUM can hook in here.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[KainOS] Unhandled render error', error, info.componentStack);
  }

  private readonly reset = () => this.setState({ error: null });

  override render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <EmptyState
        title="Algo salió mal"
        description="No pudimos mostrar esta sección. Vuelve a intentarlo; si persiste, avísanos."
        action={
          <Button variant="primary" onClick={this.reset}>
            Reintentar
          </Button>
        }
      />
    );
  }
}
